import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowUpRight, RefreshCw } from "lucide-react";
import type { SuperAdminAuditLogItem } from "@multi-chat/shared";
import { Button } from "../../components/ui/button/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import { listSuperAdminAuditLogs } from "../../api/super-admin-api.js";
import {
  SuperAdminAuditActorBadge,
  SuperAdminLogDetailDialog,
} from "./SuperAdminLogsPage.js";
import {
  auditActionLabel,
  auditEntityLabel,
  formatAuditTime,
} from "./super-admin-audit-display.js";
import {
  contactsTableHeaderLabelClass,
  superAdminTableBodyCellClass,
  superAdminTableBodyRowClass,
  superAdminTableClass,
  superAdminTableHeaderCellClass,
  superAdminTableHeadRowClass,
  superAdminTableScrollClass,
  superAdminTableWorkspaceClass,
} from "./super-admin-table-presentation.js";

export function OrganizationActivitySection({
  organizationId,
  active,
}: {
  organizationId: number;
  active: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<SuperAdminAuditLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [selected, setSelected] = useState<SuperAdminAuditLogItem | null>(null);

  async function loadActivity(): Promise<void> {
    setLoading(true);
    setError(false);
    try {
      const response = await listSuperAdminAuditLogs({
        organizationId,
        limit: 15,
      });
      setItems(response.items);
      setLoadedOnce(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setItems([]);
    setLoadedOnce(false);
    setError(false);
  }, [organizationId]);

  useEffect(() => {
    if (!active || loadedOnce || loading) return;
    void loadActivity();
  }, [active, loadedOnce, loading, organizationId]);

  return (
    <section className="grid gap-3" data-testid="super-admin-org-activity-section">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line/70 bg-surface px-4 py-3.5 shadow-sm">
        <div>
          <h2 className="m-0 text-[1rem] font-semibold tracking-[-0.01em] text-foreground">
            {t("superAdmin.detail.activity.title")}
          </h2>
          <p className="m-0 mt-1 text-[0.75rem] text-muted">
            {t("superAdmin.detail.activity.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/super-admin/logs?organizationId=${organizationId}`}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line bg-surface px-3 text-xs font-medium text-foreground no-underline transition-colors hover:bg-surface-muted"
            data-testid="super-admin-org-activity-full-logs"
          >
            <span>{t("superAdmin.detail.activity.openFullLogs")}</span>
            <ArrowUpRight size={13} className="rtl:-scale-x-100" aria-hidden="true" />
          </Link>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="size-8 p-0"
            onClick={() => void loadActivity()}
            title={t("common.refresh")}
            aria-label={t("common.refresh")}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} aria-hidden="true" />
          </Button>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div
          className={`${superAdminTableWorkspaceClass} h-40 animate-pulse`}
          data-testid="super-admin-org-activity-loading"
          aria-busy="true"
        />
      ) : error ? (
        <ProductErrorState
          testId="super-admin-org-activity-error"
          title={t("superAdmin.logs.loadErrorTitle")}
          description={t("superAdmin.logs.loadErrorBody")}
          onRetry={() => void loadActivity()}
          retryLabel={t("superAdmin.retry")}
        />
      ) : items.length === 0 ? (
        <div
          className={`${superAdminTableWorkspaceClass} px-4 py-8 text-center text-sm text-muted`}
          data-testid="super-admin-org-activity-empty"
        >
          {t("superAdmin.detail.activity.empty")}
        </div>
      ) : (
        <div className={superAdminTableWorkspaceClass}>
          <div className={superAdminTableScrollClass}>
            <table
              className={`${superAdminTableClass} min-w-[40rem]`}
              data-testid="super-admin-org-activity-table"
            >
              <thead>
                <tr className={superAdminTableHeadRowClass}>
                  <th className={superAdminTableHeaderCellClass}>
                    <span className={contactsTableHeaderLabelClass}>
                      {t("superAdmin.logs.columns.time")}
                    </span>
                  </th>
                  <th className={superAdminTableHeaderCellClass}>
                    <span className={contactsTableHeaderLabelClass}>
                      {t("superAdmin.logs.columns.action")}
                    </span>
                  </th>
                  <th className={superAdminTableHeaderCellClass}>
                    <span className={contactsTableHeaderLabelClass}>
                      {t("superAdmin.logs.columns.actor")}
                    </span>
                  </th>
                  <th className={superAdminTableHeaderCellClass}>
                    <span className={contactsTableHeaderLabelClass}>
                      {t("superAdmin.logs.columns.resource")}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={superAdminTableBodyRowClass}
                    data-testid={`super-admin-org-activity-row-${item.id}`}
                  >
                    <td className={`${superAdminTableBodyCellClass} whitespace-nowrap align-top text-muted`}>
                      <bdi>{formatAuditTime(item.occurredAt, i18n.language)}</bdi>
                    </td>
                    <td className={`${superAdminTableBodyCellClass} align-top`}>
                      <button
                        type="button"
                        className="cursor-pointer border-0 bg-transparent p-0 text-start font-medium text-foreground hover:underline focus-visible:underline"
                        onClick={() => setSelected(item)}
                      >
                        {auditActionLabel(t, item.actionType)}
                      </button>
                    </td>
                    <td className={`${superAdminTableBodyCellClass} align-top`}>
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">
                          {item.actor.displayName ??
                            (item.actor.kind === "SYSTEM"
                              ? t("superAdmin.logs.actor.system")
                              : t("superAdmin.logs.actor.unknown"))}
                        </span>
                        <SuperAdminAuditActorBadge kind={item.actor.kind} />
                      </span>
                    </td>
                    <td className={`${superAdminTableBodyCellClass} align-top text-muted`}>
                      <span>
                        {auditEntityLabel(t, item.entity.type)}
                        {item.entity.id ? (
                          <>
                            {" "}
                            <LtrValue>#{item.entity.id}</LtrValue>
                          </>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SuperAdminLogDetailDialog item={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
